import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Injector,
  Input,
  OnInit,
  PLATFORM_ID,
  Type,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { Module } from 'projects/common/model/ModuleModel';
import { AbstractComponent } from '../abstract.component';
import { PictureData, Picture } from '../picture/picture.component';
import { SliderOptions } from '../slider/slider/slider.component';
import { GridOptions } from '../grid/grid.component';
import { isPlatformBrowser } from '@angular/common';
import { EvaSize } from '@shopping-angular-components/loader';
import { SliderProOptions } from '../slider-pro/slider-pro.component';
import { ScreenType } from 'projects/common/model/ScreenType';
import { UpaTarget } from 'projects/common/services/upa.service';

export interface PictureListModuleData {
  pictures: PictureData[];
  slider_options?: SliderOptions;
  grid_options?: GridOptions;
  fixed_height?: number;
  view_full_width?: boolean;
}

interface PictureComponentParams {
  uniqueId?: string;
  data: PictureData;
  position: number;
  moduleId: string;
  moduleIndex: number;
  screenType: ScreenType;
}

@Component({
  selector: 'picture-list-component',
  template: `
    <text *ngIf="hasTextData" [data]="module" class="-eva-3-mb-xlg"></text>
    <div *ngIf="module?.data?.pictures" >
      <div *ngIf="module?.data?.slider_options" #galleryContainer>
        <div *ngIf="!showGallery" class="loaders-container">
          <loader *ngFor="let index of loaders"
              class="loader"
              [flow]="flow"
              product="nymeria-gui"
              [show]="true"
              [size]="evaSizes.MEDIUM"
          ></loader>
        </div>
        <div *ngIf="showGallery" [ngClass]="{'slider-container': !viewFullWidth}">
          <slider-pro
            [options]="sliderProOptions"
            [componentParams]="componentParams"
            [components]="components"
            [upaData]="upaData"
            [viewFullWidth]="viewFullWidth"
          ></slider-pro>
        </div>
      </div>

      <grid *ngIf="module.data.grid_options" [options]='module.data.grid_options'>
        <!-- Esto es una picture fantasma para obtener el ancho -->
        <div *ngIf="!pictures" #fakePicture ></div>
        <picture *ngFor="let picture of pictures; let i = index;" 
            [moduleId]="module.id" 
            [moduleIndex]="module.row_number" 
            [position]="i" 
            [data]="picture" 
            [screenType]="screenType" 
            [uniqueId]="module.unique_id">
        </picture>
      </grid>
    </div>

  `,
  styleUrls: ['./picture-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PictureListComponent extends AbstractComponent implements OnInit, AfterViewInit {

  @Input() override module: Module<PictureListModuleData>;
  picturesHeight: number | null = null;
  picturesWidth: number| null = null;
  sliderWidth: number;
  evaSizes = EvaSize;
  showGallery = false;
  viewFullWidth = false;
  infiniteScroll = false;
  autoScroll = false;
  pictures: PictureData[];
  loaders: number[] = [];

  sliderProOptions: SliderProOptions;
  componentParams: PictureComponentParams[] = [];
  components: Type<any>[] = [];

  rawUpaData: any;
  upaData: any;
  
  @HostBinding('class.view-full-width') get isViewFullWidth() {
    return this.viewFullWidth;
  }
  
  @ViewChild("galleryContainer") public sliderContainer: ElementRef;
  @ViewChild("fakePicture") public pictureComponent: ElementRef;

  constructor(injector: Injector,
    @Inject(PLATFORM_ID) private platformId: string) {
    super(injector);
  }
  
  get hasTextData(): boolean {
    return !!(this.module?.pretitle || this.module?.title || this.module?.subtitle);
  }

  private getItemsToShow(): number {
    return this.module.data.slider_options?.items_to_show || 
           (this.screenType === 'DESKTOP' ? 4 : 1);
  }

  ngAfterViewInit(): void {
    if (this.module?.data?.pictures) {
      if (this.module?.data?.slider_options){
        this.setFixedHeightIfPresent();
      } else {
        this.calculateGridSizes();
      }
    }
  }

  ngOnInit(): void {
    if (this.module?.data?.pictures) {
      if(this.module?.data?.slider_options) {
        this.loaders = Array.from({ length: this.getItemsToShow() }, (_, i) => i);
        this.showGallery = isPlatformBrowser(this.platformId);
        this.initializeUpaData();
        this.initializeSlider();
      } else if (!this.module.data.fixed_height && isPlatformBrowser(this.platformId)) {
        this.pictures = this.module.data.pictures;
      }
    }
  }

  private initializeSlider(): void {
    this.viewFullWidth = this.module.data.view_full_width || false;
    this.infiniteScroll = this.module.data.slider_options?.infinite_scroll || false;
    this.autoScroll = this.module.data.slider_options?.auto_scroll || false;
    
    this.setFixedHeightIfPresent();
    this.transformModelToSliderPro();
    this.initSliderProOptions();
  }

  private transformModelToSliderPro(): void {
    this.components = Array(this.module.data.pictures.length).fill(Picture);
    this.componentParams = this.module.data.pictures.map((picture, index) => ({
      uniqueId: this.module.unique_id,
      data: {
        ...picture,
        height: this.picturesHeight
      },
      position: index,
      moduleId: this.module.id,
      moduleIndex: this.module.row_number,
      screenType: this.screenType,
      upaData: this.upaData
    }));
  }

  private initSliderProOptions(): void {
    const isDesktop = this.screenType === 'DESKTOP';
    const isMobile = this.screenType === 'MOBILE';
    const itemsToShow = this.getItemsToShow();
    const itemsToMove = this.module.data.slider_options?.items_to_move ?? 1;
    const showArrows = isDesktop && !this.viewFullWidth;
    const showDots = isMobile || (isDesktop && !this.viewFullWidth);
    const width = this.sliderContainer?.nativeElement?.clientWidth || globalThis.innerWidth;
    const containerWidth = width > 1280 ? 1280 : width;
    
    this.sliderProOptions = {
      itemsToShow: { 'DEFAULT': itemsToShow },
      itemsToMove: { 'DEFAULT': itemsToMove },
      dragToMove: { 'DEFAULT': !isDesktop },
      showArrows: { 'DEFAULT': showArrows },
      showDots: { 'DEFAULT': showDots },
      dotsLighten: this.viewFullWidth,
      arrowsInnerContainer: !!this.viewFullWidth,
      infiniteScroll: { 'DEFAULT': this.infiniteScroll },
      autoScroll: { 'DEFAULT': this.autoScroll },
      containerWidth: { 'DEFAULT': containerWidth }
    };
  }

  getModuleId(): string {
    return this.module?.id;
  }

  private setFixedHeightIfPresent() {
    this.picturesHeight = this.module.data.fixed_height || (this.screenType == 'MOBILE' ? 150 : 300);
  }

  private calculateGridSizes() {
    if (this.module.data.fixed_height && isPlatformBrowser(this.platformId)) {
      this.picturesWidth = this.pictureComponent?.nativeElement?.clientWidth;
      this.picturesHeight = this.module.data.fixed_height;
      this.module.data.pictures.forEach(picture => {
        picture.height = this.picturesHeight;
        picture.width = this.picturesWidth;
        picture.media_params = {op: 'TRUNCATE'}
      });
      this.pictures = this.module.data.pictures;
    }
  }

  private initializeUpaData(): void {
    this.rawUpaData = {
      uniqueId: this.module.unique_id,
      moduleId: this.module.id,
      moduleIndex: this.module.row_number,
      target: UpaTarget.IMAGE
    };
    this.upaData = this.upaService.resolveData(this.rawUpaData);
  }
}